import { FileText, Hash, Download, Trash2 } from "lucide-react";
import { usePagination } from "@/lib/usePagination";

const formatMoney = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

/**
 * Admin invoices list — extracted from AdminPortal for clarity.
 * Props:
 *  - invoices: array of invoice records (each {id, invoice_number, client, description, period, amount, ...})
 *  - loading: bool
 *  - onDownload(invoice): handler
 *  - onDelete(id): handler
 */
export default function InvoicesTab({ invoices, loading, onDownload, onDelete }) {
  const { pageItems, controls } = usePagination(invoices, 10, "admin-invoices-pager");

  return (
    <div className="border border-[#2E2E33] bg-[#1F1F25]" data-testid="admin-invoices-list">
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#2E2E33] font-mono text-[10px] uppercase tracking-widest text-[#71717A]">
        <div className="col-span-2">Folio</div>
        <div className="col-span-3">Cliente</div>
        <div className="col-span-3">Descripción</div>
        <div className="col-span-1">Periodo</div>
        <div className="col-span-1 text-right">Monto</div>
        <div className="col-span-2 text-right">Acciones</div>
      </div>
      {loading ? (
        <div className="p-12 text-center text-[#A1A1AA] font-mono text-sm">Cargando...</div>
      ) : invoices.length === 0 ? (
        <div className="p-16 text-center">
          <FileText className="w-12 h-12 text-[#2E2E33] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">No hay facturas. Suba la primera.</p>
        </div>
      ) : (
        <>
          {pageItems.map((inv) => (
            <div key={inv.id} className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 border-b border-[#2E2E33] last:border-b-0 hover:bg-[#26262C] items-center">
              <div className="md:col-span-2 font-mono text-sm flex items-center gap-2">
                <Hash className="w-3 h-3 text-[#F59E0B]" />
                {inv.invoice_number || inv.id.slice(0, 8).toUpperCase()}
              </div>
              <div className="md:col-span-3 text-sm">
                <div className="text-white">{inv.client?.name || inv.client?.email}</div>
                <div className="text-[#71717A] text-xs font-mono">{inv.client?.email}</div>
              </div>
              <div className="md:col-span-3 text-sm text-[#D4D4D8]">{inv.description}</div>
              <div className="md:col-span-1 text-xs text-[#A1A1AA] font-mono">{inv.period || "—"}</div>
              <div className="md:col-span-1 text-right font-heading font-bold text-[#F59E0B]">{formatMoney(inv.amount)}</div>
              <div className="md:col-span-2 flex md:justify-end gap-2">
                <button onClick={() => onDownload(inv)} className="border border-[#2E2E33] hover:border-[#F59E0B] hover:text-[#F59E0B] px-3 py-2 transition-colors" data-testid={`admin-download-${inv.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => onDelete(inv.id)} className="border border-[#2E2E33] hover:border-[#EF4444] hover:text-[#EF4444] px-3 py-2 transition-colors" data-testid={`admin-delete-${inv.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          {controls}
        </>
      )}
    </div>
  );
}
